import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon,
  AcademicCapIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  PencilIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getTrainingPlan, upsertTrainingPlanItem } from "../../../../api/hse/competency";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Training Plan - Annual training plan (management)
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyTrainingPlan = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  
  const [trainingPlan, setTrainingPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    year: new Date().getFullYear(),
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchTrainingPlan();
  }, [filters]);

  const fetchTrainingPlan = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        year: filters.year || new Date().getFullYear(),
      };
      
      const response = await getTrainingPlan(apiFilters);
      setTrainingPlan(response.training_plan_items || response.items || []);
    } catch (err) {
      console.error("Error fetching training plan:", err);
      setError(true);
      toast.error(t("Kunne ikke laste opplæringsplan"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      year: newFilters.year || new Date().getFullYear(),
    });
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste opplæringsplan")]} />;
  }

  return (
    <DashboardHeader
      title={t("Opplæringsplan")}
      subtitle={t("Årlig opplæringsplan (ledelse)")}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i opplæringsplan...")}
          />

          {/* Training Plan List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("Opplæringsplan")} {filters.year} ({trainingPlan.length})
                </h3>
                {canManage && (
                  <button
                    onClick={() => navigate("/dashboard/hse-ks/competency/training-plan/new")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t("Legg til opplæring")}
                  </button>
                )}
              </div>
              
              {trainingPlan.length === 0 ? (
                <ResourceEmpty message={t("Ingen opplæringsplan registrert")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Ansatt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Kompetanse")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Planlagt dato")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {trainingPlan.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div className="font-medium text-gray-900">
                                {item.employee_name || item.employee?.name || item.employee?.email}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-3" />
                              <div className="font-medium text-gray-900">
                                {item.competency_definition?.name || item.competency_name}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {item.planned_date ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(item.planned_date).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {item.status === "completed" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t("Fullført")}
                              </span>
                            ) : item.status === "in_progress" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {t("Pågående")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t("Planlagt")}
                              </span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end space-x-2">
                              {canManage && (
                                <Link
                                  to={`/dashboard/hse-ks/competency/training-plan/${item.id}/edit`}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  {t("Rediger")}
                                </Link>
                              )}
                              <Link
                                to={`/dashboard/hse-ks/competency/employees/${item.employee_id || item.employee?.id}`}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                {t("Vis ansatt")}
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

